import {FC, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {InvoiceListSummaryBreakdown} from '../../modules/apps/invoice-management/invoices-list/core/_models'
import {
  getInvoiceListSummary,
  getInvoiceListSummaryQueryKey,
  INVOICE_LIST_SUMMARY_CACHE_TIME_MS,
  INVOICE_LIST_SUMMARY_STALE_TIME_MS,
  shouldRetryInvoiceListSummary,
} from '../../modules/apps/invoice-management/invoices-list/core/_requests'
import {
  formatInvoiceAmount,
  formatInvoiceCount,
} from '../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {InvoiceSeasonSelect} from '../../modules/apps/invoice-management/invoices-list/components/header/InvoiceSeasonSelect'
import {
  InvoiceSeasonUserSetting,
  getInvoiceSeasonFromSetting,
  isAllInvoiceSeasonValue,
  isMultiInvoiceSeasonValue,
  loadInvoiceSeasonUserSetting,
  normalizeInvoiceSeasonValue,
  persistInvoiceSeasonSelection,
  readCachedInvoiceSeasonSelection,
  splitInvoiceSeasonValue,
} from '../../modules/apps/invoice-management/invoices-list/core/invoiceSeasonSelection'
import {useAuth} from '../../modules/auth'
import {useIsMobileViewport} from '../../hooks/useMediaQuery'

const DASHBOARD_INVOICE_BASE_MODE = 1

const colorHex = (color?: string) => {
  switch (color) {
    case 'success':
      return '#16a36b'
    case 'warning':
      return '#d97706'
    case 'danger':
      return '#dc2626'
    case 'info':
      return '#2563eb'
    case 'primary':
      return '#4f46e5'
    case 'secondary':
      return '#64748b'
    default:
      return '#7e8a9a'
  }
}

const formatPercent = (value?: number) => formatInvoiceAmount(value || 0)

const formatExactAmount = (value?: number, exactValue?: string) =>
  formatInvoiceAmount(exactValue ?? value ?? 0)

const getBreakdownItems = (items?: InvoiceListSummaryBreakdown[]) =>
  (items || []).filter((item) => Number(item.amount || 0) > 0 || Number(item.count || 0) > 0)

const InvoiceSeasonDashboardSummary: FC = () => {
  const {currentUser} = useAuth()
  const isMobileViewport = useIsMobileViewport()
  const seasonUser = useMemo(
    () => (currentUser?.id ? {id: currentUser.id, cpi: currentUser.cpi} : undefined),
    [currentUser?.cpi, currentUser?.id]
  )
  const supportSessionId = currentUser?.support?.session_id
  const [invoiceSeason, setInvoiceSeason] = useState('')
  const [seasonSetting, setSeasonSetting] = useState<InvoiceSeasonUserSetting | string | null>()

  useEffect(() => {
    let mounted = true
    setInvoiceSeason('')
    setSeasonSetting(undefined)

    if (!seasonUser?.id) {
      return () => {
        mounted = false
      }
    }

    const cachedSeason = readCachedInvoiceSeasonSelection(seasonUser, DASHBOARD_INVOICE_BASE_MODE)
    if (cachedSeason) {
      setInvoiceSeason(cachedSeason)
    }

    loadInvoiceSeasonUserSetting()
      .then((setting) => {
        if (!mounted) {
          return
        }

        setSeasonSetting(setting || null)
        const storedSeason = getInvoiceSeasonFromSetting(
          setting,
          seasonUser,
          DASHBOARD_INVOICE_BASE_MODE
        )
        if (storedSeason) {
          setInvoiceSeason(storedSeason)
        }
      })
      .catch(() => {
        if (mounted) {
          setSeasonSetting(null)
        }
      })

    return () => {
      mounted = false
    }
  }, [seasonUser])

  const requestedSeason = normalizeInvoiceSeasonValue(invoiceSeason) || undefined
  const {
    data: summary,
    isFetching,
    refetch,
  } = useQuery(
    getInvoiceListSummaryQueryKey(
      currentUser?.cpi || 0,
      DASHBOARD_INVOICE_BASE_MODE,
      requestedSeason
    ),
    () => getInvoiceListSummary(DASHBOARD_INVOICE_BASE_MODE, requestedSeason),
    {
      cacheTime: INVOICE_LIST_SUMMARY_CACHE_TIME_MS,
      staleTime: INVOICE_LIST_SUMMARY_STALE_TIME_MS,
      enabled: Boolean(seasonUser?.id || supportSessionId),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: shouldRetryInvoiceListSummary,
    }
  )

  const activeSeasonKey =
    normalizeInvoiceSeasonValue(invoiceSeason) || summary?.selectedSeason?.key || ''
  const activeSeasonKeys = splitInvoiceSeasonValue(activeSeasonKey)
  const activeSeasonIsAll = isAllInvoiceSeasonValue(activeSeasonKey)
  const activeSeasonIsMulti = isMultiInvoiceSeasonValue(activeSeasonKey)
  const activeSeason =
    !activeSeasonIsAll && activeSeasonKeys.length === 1
      ? summary?.seasons?.find((season) => season.key === activeSeasonKeys[0]) ||
        summary?.selectedSeason
      : summary?.selectedSeason

  const selectedSeasonLabel = activeSeasonIsAll
    ? 'همه فصل‌ها'
    : activeSeasonIsMulti
    ? activeSeason?.name || `${activeSeasonKeys.length} فصل انتخابی`
    : activeSeason?.name || 'فصل جاری'
  const selectedSeasonRange =
    summary?.selectedSeason?.fromDate && summary?.selectedSeason?.toDate
      ? `${summary.selectedSeason.fromDate} تا ${summary.selectedSeason.toDate}`
      : ''

  const paymentItems = useMemo(() => getBreakdownItems(summary?.payments), [summary?.payments])
  const trendRows = useMemo(() => summary?.trend || [], [summary?.trend])
  const hasSummary = Number(summary?.totalCount || 0) > 0 || Number(summary?.totalAmount || 0) > 0

  const paymentSeries = paymentItems.map((item) => Number(item.amount || 0))
  const paymentTotal = paymentSeries.reduce((sum, item) => sum + Number(item || 0), 0)
  const displayedPaymentTotal = summary?.paymentTotalExact ?? summary?.paymentTotal ?? paymentTotal
  const paymentGradient = useMemo(() => {
    if (!paymentItems.length || paymentTotal <= 0) {
      return '#edf2f7'
    }

    let cursor = 0
    const segments = paymentItems.map((item) => {
      const size = (Number(item.amount || 0) / paymentTotal) * 100
      const start = cursor
      cursor += size
      return `${colorHex(item.color)} ${start}% ${cursor}%`
    })
    return `conic-gradient(${segments.join(', ')})`
  }, [paymentItems, paymentTotal])
  const maxTrendAmount = Math.max(...trendRows.map((item) => Number(item.amount || 0)), 1)
  const maxTrendCount = Math.max(...trendRows.map((item) => Number(item.count || 0)), 1)
  const compactTrendRows = trendRows.slice(-8)

  const getTrendHeight = (amount: number) => {
    const percent = (Number(amount || 0) / maxTrendAmount) * 100
    return `${Math.max(percent, amount > 0 ? 8 : 2)}%`
  }

  const getTrendCountHeight = (count: number) => {
    const percent = (Number(count || 0) / maxTrendCount) * 100
    return `${Math.max(percent, count > 0 ? 8 : 2)}%`
  }

  const getTrendShortLabel = (label?: string) =>
    (label || '')
      .replace('بهار', 'ب')
      .replace('تابستان', 'ت')
      .replace('پاییز', 'پ')
      .replace('زمستان', 'ز')

  const changeSeason = (nextSeason: string) => {
    const normalizedSeason = normalizeInvoiceSeasonValue(nextSeason)
    setInvoiceSeason(normalizedSeason)
    if (!seasonUser?.id) {
      return
    }
    persistInvoiceSeasonSelection(
      seasonUser,
      DASHBOARD_INVOICE_BASE_MODE,
      normalizedSeason,
      seasonSetting || null
    )
      .then((nextSetting) => {
        if (nextSetting) {
          setSeasonSetting(nextSetting)
        }
      })
      .catch(() => {})
  }

  return (
    <section className='mp-panel mp-dashboard-summary'>
      <div className='mp-dashboard-summary__head'>
        <div>
          <span>تحلیل فروش</span>
          <h2>نمای فصل‌های صورتحساب</h2>
          <small>{selectedSeasonRange || selectedSeasonLabel}</small>
          <small>بدون صورتحساب‌های ابطالی و ابطال‌شده</small>
        </div>

        <div className='mp-dashboard-summary__controls'>
          <InvoiceSeasonSelect
            seasons={summary?.seasons || []}
            value={activeSeasonKey}
            loading={isFetching}
            label='فصل‌های صورتحساب'
            onChange={changeSeason}
          />
          <button
            type='button'
            className='btn btn-icon btn-light'
            title='بروزرسانی'
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <i className='las la-sync fs-2' />
          </button>
        </div>
      </div>

      <div className='mp-dashboard-summary__kpis'>
        <div>
          <span>جمع خالص</span>
          <strong>{formatExactAmount(summary?.totalAmount, summary?.totalAmountExact)}</strong>
          <small>ریال</small>
        </div>
        <div>
          <span>تعداد صورتحساب</span>
          <strong>{formatInvoiceCount(summary?.totalCount || 0)}</strong>
          <small>{selectedSeasonLabel}</small>
        </div>
        <div>
          <span>ارزش افزوده</span>
          <strong>{formatExactAmount(summary?.vatAmount, summary?.vatAmountExact)}</strong>
          <small>ریال</small>
        </div>
        <div>
          <span>نقدی دریافت‌شده</span>
          <strong>{formatExactAmount(summary?.cashAmount, summary?.cashAmountExact)}</strong>
          <small>ریال</small>
        </div>
      </div>

      {hasSummary && !isMobileViewport ? (
        <div className='mp-dashboard-summary__charts'>
          <div className='mp-dashboard-payment'>
            <div className='mp-dashboard-chart-title'>
              <span>ترکیب مبلغ</span>
              <strong>
                {formatExactAmount(summary?.totalAmount, summary?.totalAmountExact)} ریال
              </strong>
            </div>
            {paymentSeries.some((item) => item > 0) ? (
              <div className='mp-dashboard-payment__body'>
                <div className='mp-dashboard-payment__ring' style={{background: paymentGradient}}>
                  <div>
                    <span>ترکیب</span>
                    <strong>{formatInvoiceAmount(displayedPaymentTotal)}</strong>
                  </div>
                </div>
                <div className='mp-dashboard-payment__legend'>
                  {paymentItems.map((item) => (
                    <div key={item.key}>
                      <span>
                        <i style={{backgroundColor: colorHex(item.color)}} />
                        {item.label}
                      </span>
                      <strong>{formatExactAmount(item.amount, item.amountExact)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className='mp-dashboard-empty'>داده مبلغی برای نمودار وجود ندارد.</div>
            )}
          </div>

          <div className='mp-dashboard-trend'>
            <div className='mp-dashboard-chart-title'>
              <span>
                {activeSeasonIsAll || activeSeasonIsMulti ? 'روند فصل‌ها' : 'مقایسه فصل انتخابی'}
              </span>
              <strong>{selectedSeasonLabel}</strong>
            </div>
            {compactTrendRows.length > 0 ? (
              <div className='mp-dashboard-trend__bars'>
                {compactTrendRows.map((item) => (
                  <div key={`${item.date}-${item.label}`} className='mp-dashboard-trend__item'>
                    <div className='mp-dashboard-trend__bar'>
                      <span
                        className='mp-dashboard-trend__bar-amount'
                        style={{height: getTrendHeight(Number(item.amount || 0))}}
                      />
                      <span
                        className='mp-dashboard-trend__bar-count'
                        style={{height: getTrendCountHeight(Number(item.count || 0))}}
                      />
                    </div>
                    <small>{getTrendShortLabel(item.shortLabel || item.label)}</small>
                    <strong>{formatExactAmount(item.amount, item.amountExact)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className='mp-dashboard-empty'>برای فصل انتخابی داده نموداری وجود ندارد.</div>
            )}
          </div>
        </div>
      ) : !hasSummary ? (
        <div className='mp-dashboard-empty mp-dashboard-empty--wide'>
          {isFetching ? 'در حال دریافت اطلاعات فصل...' : 'برای فصل انتخابی داده‌ای ثبت نشده است.'}
        </div>
      ) : null}
    </section>
  )
}

export {InvoiceSeasonDashboardSummary}
